<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, reactive, ref, watch } from 'vue';
import camelcaseKeys from 'camelcase-keys';
import AccessAssetsAPI from 'dashboard/api/accessAssets';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import LaisDrawer from 'dashboard/components-next/LaisDrawer.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  imovelId: { type: [Number, String], default: '' },
  initialAssetId: { type: [Number, String], default: '' },
  side: { type: String, default: 'right' },
  title: { type: String, default: 'Custódia e chaves' },
});

const emit = defineEmits(['close', 'changed']);

const assets = ref([]);
const timelineEvents = ref([]);
const selectedAssetId = ref('');
const isLoading = ref(false);
const isTimelineLoading = ref(false);
const actionLoading = ref('');
const errorMessage = ref('');
const successMessage = ref('');
const actionReason = ref('');
const pendingCustodyAction = ref('');
const showCustodyActionConfirm = ref(false);
const createForm = reactive({
  assetUid: '',
  assetType: 'key',
  label: '',
});

const ASSET_TYPE_OPTIONS = [
  { value: 'key', label: 'Chave' },
  { value: 'lockbox', label: 'Lockbox' },
  { value: 'access_card', label: 'Cartão' },
  { value: 'remote_control', label: 'Controle' },
  { value: 'password', label: 'Senha' },
  { value: 'other', label: 'Outro' },
];

const STATUS_META = {
  available: {
    label: 'Disponível',
    className: 'bg-emerald-50 text-emerald-700',
  },
  reserved: { label: 'Reservada', className: 'bg-amber-50 text-amber-700' },
  checked_out: { label: 'Retirada', className: 'bg-blue-50 text-blue-700' },
  return_pending: {
    label: 'A devolver',
    className: 'bg-rose-50 text-rose-700',
  },
  returned: { label: 'Devolvida', className: 'bg-slate-100 text-slate-700' },
  blocked: { label: 'Bloqueada', className: 'bg-rose-50 text-rose-700' },
  lost: { label: 'Extraviada', className: 'bg-rose-50 text-rose-700' },
  damaged: { label: 'Danificada', className: 'bg-orange-50 text-orange-700' },
  divergent: { label: 'Divergente', className: 'bg-orange-50 text-orange-700' },
  retired: {
    label: 'Retirada do uso',
    className: 'bg-slate-100 text-slate-700',
  },
};

const EVENT_LABELS = {
  registered: 'Ativo registrado',
  reserved: 'Reserva criada',
  reservation_canceled: 'Reserva cancelada',
  picked_up: 'Retirada confirmada',
  return_requested: 'Devolução solicitada',
  returned: 'Devolução confirmada',
  not_delivered: 'Chave não entregue',
  blocked: 'Bloqueio registrado',
  unblocked: 'Desbloqueio registrado',
  lost: 'Extravio',
  damaged: 'Dano',
  divergence_reported: 'Divergência',
  secret_rotated: 'Segredo rotacionado',
  secret_revealed: 'Segredo revelado',
  scan: 'Leitura/scan',
  note: 'Nota',
};

const selectedAsset = computed(
  () =>
    assets.value.find(
      asset => String(asset.id) === String(selectedAssetId.value)
    ) ||
    assets.value[0] ||
    null
);
const statusMeta = computed(
  () =>
    STATUS_META[selectedAsset.value?.status] || {
      label: selectedAsset.value?.status || 'Sem status',
      className: 'bg-slate-100 text-slate-700',
    }
);
const canSubmitAction = computed(
  () => Boolean(selectedAsset.value?.id) && actionReason.value.trim().length > 2
);
const canCreateAsset = computed(() => createForm.assetUid.trim().length > 1);
const hasAssets = computed(() => assets.value.length > 0);
const selectedCustodian = computed(
  () => selectedAsset.value?.currentCustodian || {}
);
const selectedLabel = computed(() => {
  const asset = selectedAsset.value;
  if (!asset) return 'Nenhum ativo selecionado';
  return asset.label || asset.assetUid || `Ativo #${asset.id}`;
});

const normalizePayload = payload =>
  camelcaseKeys(payload || {}, { deep: true });

const idempotencyKey = action =>
  `key-custody:${action}:${selectedAsset.value?.id || props.imovelId}:${Date.now()}`;

const custodyActionConfirmTitle = computed(() =>
  pendingCustodyAction.value === 'block'
    ? 'Bloquear chave?'
    : 'Desbloquear chave?'
);

const custodyActionConfirmDescription = computed(() =>
  pendingCustodyAction.value === 'block'
    ? 'Confirmar bloqueio desta chave? O motivo será gravado no ledger.'
    : 'Confirmar desbloqueio desta chave? O motivo será gravado no ledger.'
);

const assetTypeLabel = value =>
  ASSET_TYPE_OPTIONS.find(option => option.value === value)?.label || value;

const statusLabel = value => STATUS_META[value]?.label || value || 'Sem status';

const eventLabel = event =>
  EVENT_LABELS[event?.eventType] || event?.eventType || 'Evento';

const formatDateTime = value => {
  if (!value) return 'Sem horário';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Horário inválido';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const partyLabel = party =>
  party?.name ||
  party?.email ||
  (party?.id ? `${party.type || 'Ator'} #${party.id}` : 'Não informado');

const payloadReason = event =>
  event?.payload?.reason || event?.payload?.cancelReason || '';

const payloadStatusLine = event => {
  const previous = event?.payload?.previousStatus || event?.fromStatus;
  const next = event?.toStatus;
  if (!previous && !next) return '';
  return `${statusLabel(previous)} → ${statusLabel(next)}`;
};

const loadTimeline = async assetId => {
  if (!assetId) {
    timelineEvents.value = [];
    return;
  }

  isTimelineLoading.value = true;
  try {
    const { data } = await AccessAssetsAPI.timeline(assetId);
    timelineEvents.value = normalizePayload(data.payload || {}).events || [];
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error || 'Não foi possível carregar o ledger.';
  } finally {
    isTimelineLoading.value = false;
  }
};

const loadAssets = async () => {
  if (!props.show || !props.imovelId) return;

  isLoading.value = true;
  errorMessage.value = '';
  successMessage.value = '';
  try {
    const { data } = await AccessAssetsAPI.forImovel(props.imovelId);
    assets.value = normalizePayload(data.payload || data || []);
    selectedAssetId.value =
      props.initialAssetId ||
      selectedAssetId.value ||
      assets.value[0]?.id ||
      '';
    await loadTimeline(selectedAsset.value?.id);
  } catch (error) {
    assets.value = [];
    timelineEvents.value = [];
    errorMessage.value =
      error?.response?.data?.error || 'Não foi possível carregar chaves.';
  } finally {
    isLoading.value = false;
  }
};

const selectAsset = asset => {
  selectedAssetId.value = asset?.id || '';
  successMessage.value = '';
  errorMessage.value = '';
  loadTimeline(asset?.id);
};

const closeCustodyActionConfirm = () => {
  showCustodyActionConfirm.value = false;
  pendingCustodyAction.value = '';
};

const runCustodyAction = async action => {
  if (!canSubmitAction.value) return;
  pendingCustodyAction.value = action;
  showCustodyActionConfirm.value = true;
};

const confirmCustodyAction = async () => {
  const action = pendingCustodyAction.value;

  if (!action || !canSubmitAction.value) {
    closeCustodyActionConfirm();
    return;
  }

  actionLoading.value = action;
  errorMessage.value = '';
  successMessage.value = '';
  try {
    const request =
      action === 'block'
        ? (id, payload) => AccessAssetsAPI.block(id, payload)
        : (id, payload) => AccessAssetsAPI.unblock(id, payload);
    const { data } = await request(selectedAsset.value.id, {
      reason: actionReason.value.trim(),
      idempotency_key: idempotencyKey(action),
    });
    const updatedAsset = normalizePayload(data.payload || {});
    assets.value = assets.value.map(asset =>
      asset.id === updatedAsset.id ? updatedAsset : asset
    );
    selectedAssetId.value = updatedAsset.id;
    actionReason.value = '';
    successMessage.value =
      action === 'block' ? 'Bloqueio registrado.' : 'Desbloqueio registrado.';
    await loadTimeline(updatedAsset.id);
    emit('changed', updatedAsset);
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error ||
      (action === 'block'
        ? 'Não foi possível bloquear a chave.'
        : 'Não foi possível desbloquear a chave.');
  } finally {
    actionLoading.value = '';
    closeCustodyActionConfirm();
  }
};

const createAsset = async () => {
  if (!canCreateAsset.value || !props.imovelId) return;

  actionLoading.value = 'create';
  errorMessage.value = '';
  successMessage.value = '';
  try {
    const { data } = await AccessAssetsAPI.createForImovel(props.imovelId, {
      asset_uid: createForm.assetUid.trim(),
      asset_type: createForm.assetType,
      label: createForm.label.trim(),
      idempotency_key: `key-custody:create:${props.imovelId}:${Date.now()}`,
    });
    const createdAsset = normalizePayload(data.payload || {});
    assets.value = [createdAsset, ...assets.value];
    selectedAssetId.value = createdAsset.id;
    createForm.assetUid = '';
    createForm.assetType = 'key';
    createForm.label = '';
    successMessage.value = 'Ativo de acesso registrado.';
    await loadTimeline(createdAsset.id);
    emit('changed', createdAsset);
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.error || 'Não foi possível registrar o ativo.';
  } finally {
    actionLoading.value = '';
  }
};

watch(
  () => [props.show, props.imovelId, props.initialAssetId],
  () => {
    if (props.show) loadAssets();
  },
  { immediate: true }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisDrawer
    :show="show"
    :title="title"
    :side="side"
    size="lg"
    @close="emit('close')"
  >
    <div class="grid gap-4">
      <div
        v-if="successMessage"
        class="rounded-lais-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] font-semibold text-emerald-800"
      >
        {{ successMessage }}
      </div>
      <div
        v-if="errorMessage"
        class="rounded-lais-sm border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] font-semibold text-rose-800"
      >
        {{ errorMessage }}
      </div>

      <div v-if="isLoading" class="grid min-h-[220px] place-items-center">
        <Spinner :size="32" class="text-lais-primary" />
      </div>

      <EmptyStateLayout
        v-else-if="!hasAssets"
        title="Nenhum ativo de acesso"
        subtitle="Cadastre a chave, lockbox ou cartão que será auditado neste imóvel."
        :show-backdrop="false"
      />

      <template v-else>
        <div class="flex gap-2 overflow-x-auto pb-1">
          <button
            v-for="asset in assets"
            :key="asset.id"
            type="button"
            class="shrink-0 rounded-lais-sm border px-3 py-2 text-left text-[12px] font-semibold"
            :class="
              String(asset.id) === String(selectedAsset?.id)
                ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                : 'border-lais-border bg-white text-lais-text-body'
            "
            @click="selectAsset(asset)"
          >
            <span class="block">{{ asset.label || asset.assetUid }}</span>
            <span class="mt-1 block text-[11px] text-lais-text-muted">
              {{ assetTypeLabel(asset.assetType) }} ·
              {{ statusLabel(asset.status) }}
            </span>
          </button>
        </div>

        <section
          class="grid gap-3 rounded-lais border border-lais-border bg-white p-4"
        >
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div class="min-w-0">
              <p class="text-[15px] font-semibold text-lais-text">
                {{ selectedLabel }}
              </p>
              <p class="mt-1 text-[12px] text-lais-text-muted">
                {{ assetTypeLabel(selectedAsset?.assetType) }} · UID
                {{ selectedAsset?.assetUid }}
              </p>
            </div>
            <span
              class="rounded-full px-2.5 py-1 text-[11px] font-semibold"
              :class="statusMeta.className"
            >
              {{ statusMeta.label }}
            </span>
          </div>
          <dl class="grid gap-2 text-[12px] sm:grid-cols-2">
            <div class="rounded-lais-sm bg-lais-bg-soft px-3 py-2">
              <dt class="font-semibold text-lais-text-muted">
                Responsável atual
              </dt>
              <dd class="mt-1 font-semibold text-lais-text">
                {{ partyLabel(selectedCustodian) }}
              </dd>
            </div>
            <div class="rounded-lais-sm bg-lais-bg-soft px-3 py-2">
              <dt class="font-semibold text-lais-text-muted">Último evento</dt>
              <dd class="mt-1 font-semibold text-lais-text">
                {{ formatDateTime(selectedAsset?.lastEventAt) }}
              </dd>
            </div>
          </dl>
          <label class="grid gap-1">
            <span
              class="text-[11px] font-semibold uppercase text-lais-text-muted"
            >
              Motivo da ação
            </span>
            <textarea
              v-model="actionReason"
              rows="3"
              class="rounded-lais border border-lais-border px-3 py-2 text-[14px]"
              placeholder="Informe o motivo operacional"
            />
          </label>
          <div class="flex flex-col gap-2 sm:flex-row">
            <LaisPrimaryButton
              v-if="selectedAsset?.status !== 'blocked'"
              icon="i-lucide-lock"
              :disabled="!canSubmitAction"
              :is-loading="actionLoading === 'block'"
              @click="runCustodyAction('block')"
            >
              Bloquear chave
            </LaisPrimaryButton>
            <LaisPrimaryButton
              v-else
              icon="i-lucide-unlock"
              :disabled="!canSubmitAction"
              :is-loading="actionLoading === 'unblock'"
              @click="runCustodyAction('unblock')"
            >
              Desbloquear chave
            </LaisPrimaryButton>
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-refresh-cw"
              :is-loading="isTimelineLoading"
              @click="loadTimeline(selectedAsset?.id)"
            >
              Atualizar ledger
            </LaisPrimaryButton>
          </div>
        </section>

        <section class="grid gap-3">
          <div class="flex items-center justify-between gap-3">
            <h3 class="text-[15px] font-semibold text-lais-text">
              Ledger de custódia
            </h3>
            <span class="text-[12px] font-semibold text-lais-text-muted">
              {{ timelineEvents.length }} evento(s)
            </span>
          </div>
          <div
            v-if="isTimelineLoading"
            class="grid min-h-[120px] place-items-center"
          >
            <Spinner :size="28" class="text-lais-primary" />
          </div>
          <div
            v-else-if="timelineEvents.length === 0"
            class="rounded-lais border border-dashed border-lais-border px-3 py-5 text-center text-[13px] text-lais-text-muted"
          >
            Ainda não há eventos para este ativo.
          </div>
          <ol v-else class="grid gap-3">
            <li
              v-for="event in timelineEvents"
              :key="event.id"
              class="rounded-lais border border-lais-border bg-white px-3 py-3"
            >
              <div class="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p class="text-[13px] font-semibold text-lais-text">
                    {{ eventLabel(event) }}
                  </p>
                  <p class="mt-1 text-[12px] text-lais-text-muted">
                    {{ formatDateTime(event.occurredAt) }}
                  </p>
                </div>
                <span
                  class="rounded-full bg-lais-bg-soft px-2 py-1 text-[11px] font-semibold text-lais-text-muted"
                >
                  #{{ event.sequence }}
                </span>
              </div>
              <dl class="mt-3 grid gap-2 text-[12px] sm:grid-cols-2">
                <div>
                  <dt class="font-semibold text-lais-text-muted">Ator</dt>
                  <dd class="text-lais-text">{{ partyLabel(event.actor) }}</dd>
                </div>
                <div>
                  <dt class="font-semibold text-lais-text-muted">
                    Responsável
                  </dt>
                  <dd class="text-lais-text">
                    {{ partyLabel(event.custodian) }}
                  </dd>
                </div>
              </dl>
              <p
                v-if="payloadReason(event)"
                class="mt-2 text-[12px] text-lais-text-body"
              >
                <span class="font-semibold text-lais-text">Motivo:</span>
                {{ payloadReason(event) }}
              </p>
              <p
                v-if="payloadStatusLine(event)"
                class="mt-1 text-[12px] text-lais-text-muted"
              >
                Payload redigido: {{ payloadStatusLine(event) }}
              </p>
              <p
                v-if="event.evidence?.length"
                class="mt-1 text-[12px] text-lais-text-muted"
              >
                {{ event.evidence.length }} evidência(s) redigida(s)
              </p>
            </li>
          </ol>
        </section>
      </template>

      <section
        class="grid gap-3 rounded-lais border border-lais-border bg-lais-bg-soft p-4"
      >
        <div>
          <h3 class="text-[15px] font-semibold text-lais-text">
            Registrar ativo
          </h3>
          <p class="mt-1 text-[12px] text-lais-text-muted">
            Use quando a chave física ainda não existir no ledger canônico.
          </p>
        </div>
        <div class="grid gap-3 sm:grid-cols-3">
          <label class="grid gap-1 sm:col-span-1">
            <span
              class="text-[11px] font-semibold uppercase text-lais-text-muted"
            >
              UID
            </span>
            <input
              v-model="createForm.assetUid"
              class="h-10 rounded-lais border border-lais-border px-3 text-[14px]"
              placeholder="LOCKBOX-101"
            />
          </label>
          <label class="grid gap-1 sm:col-span-1">
            <span
              class="text-[11px] font-semibold uppercase text-lais-text-muted"
            >
              Tipo
            </span>
            <select
              v-model="createForm.assetType"
              class="h-10 rounded-lais border border-lais-border px-3 text-[14px]"
            >
              <option
                v-for="option in ASSET_TYPE_OPTIONS"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="grid gap-1 sm:col-span-1">
            <span
              class="text-[11px] font-semibold uppercase text-lais-text-muted"
            >
              Rótulo
            </span>
            <input
              v-model="createForm.label"
              class="h-10 rounded-lais border border-lais-border px-3 text-[14px]"
              placeholder="Portaria bloco A"
            />
          </label>
        </div>
        <div>
          <LaisPrimaryButton
            icon="i-lucide-key-round"
            :disabled="!canCreateAsset"
            :is-loading="actionLoading === 'create'"
            @click="createAsset"
          >
            Cadastrar no ledger
          </LaisPrimaryButton>
        </div>
      </section>
    </div>
  </LaisDrawer>

  <LaisConfirmModal
    :show="showCustodyActionConfirm"
    :title="custodyActionConfirmTitle"
    :description="custodyActionConfirmDescription"
    :confirm-text="
      pendingCustodyAction === 'block' ? 'Bloquear' : 'Desbloquear'
    "
    :is-loading="Boolean(actionLoading)"
    @close="closeCustodyActionConfirm"
    @confirm="confirmCustodyAction"
  />
</template>
